import { LanguageToggle, Typography } from '@/components/atoms';
import { ArrowRightIcon } from '@/components/atoms/icons';
import {
  LANDING_PAGE_ACTION,
  LANDING_PAGE_SCREEN,
} from '@/core/constants/dynatraceCatalog';
import { getColors } from '@/core/constants/uiColors';
import { useTranslation, useWebViewport } from '@/core/hooks';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useI18nContext } from '@/core/providers/I18nProvider';
import {
  landingSpaceDetailProperties,
  trackCatalogEvent,
} from '@/core/services/trackDynatraceCatalog';
import { webViewDirProps } from '@/core/utils/layoutDirection';
import { openPhoneDialer } from '@/core/utils/phoneUtils';
import { BREAKPOINTS } from '@/core/utils/responsive';
import { WELCOME_PATH } from '@/modules/welcome/utils/welcomeLoginQuery';
import { router } from 'expo-router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Image,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BloomCmsIcon } from '../components/BloomCmsIcon';
import { BloomSpaceCard } from '../components/BloomSpaceCard';
import { BloomSpacePhoneIcon } from '../components/BloomSpacePhoneIcon';
import { BloomUniversalLanguageHeader } from '../components/BloomUniversalLanguageHeader';
import {
  BloomUniversalPhoneMosaic,
  getMediumMosaicHeight,
  PHONE_TALL_FACILITIES_CARD,
} from '../components/BloomUniversalPhoneMosaic';
import { BloomWordmark } from '../components/BloomWordmark';
import { resolveBloomUniversalSpaceHref } from '../constants/routes';
import { useBloomUniversalHubContent } from '../hooks/useBloomUniversalHubContent';
import { resolveHubExploreCopy } from '../services/mapBloomUniversalContent';
import {
  bloomSpaceGridHeight,
  placeBloomSpaceGrid,
} from '../utils/placeBloomSpaceGrid';
import { resolveBloomUniversalImageUrl } from '../utils/resolveImageUrl';

const bloomColors = getColors();

function HubCtaArrow({
  isRTL,
  arrowIconUrl,
  color = bloomColors.bloomUniversalInkStrong,
}: Readonly<{ isRTL: boolean; arrowIconUrl?: string; color?: string }>) {
  return (
    <View style={isRTL ? styles.chevronFlip : undefined}>
      <BloomCmsIcon
        url={arrowIconUrl}
        size={13}
        fallback={<ArrowRightIcon size={13} color={color} />}
      />
    </View>
  );
}

/** Below this: phone stack. Medium: from here until landscape {@link HUB_DESKTOP_MIN_WIDTH}. */
const PHONE_MAX = 700;
/** Laptops (landscape, ≥1024 wide) use desktop mosaic — not the medium hero + bento. */
const HUB_DESKTOP_MIN_WIDTH = BREAKPOINTS.TABLET;

/** Portrait tablets (e.g. iPad Pro 13 @ 1032×1376) use medium hub, not desktop mosaic. */
function isPortraitTabletHubViewport(width: number, height: number): boolean {
  return width >= HUB_DESKTOP_MIN_WIDTH && height > width;
}

function isLargeDesktopHubViewport(width: number, height: number): boolean {
  return width >= HUB_DESKTOP_MIN_WIDTH && !isPortraitTabletHubViewport(width, height);
}

/** Inner horizontal inset for medium hub (full-bleed hero + sheet, like phone). */
const MEDIUM_HUB_EDGE_INSET = 16;
/** Taller mosaic rows on portrait tablet medium layout (default medium cap is 140). */
const MEDIUM_PORTRAIT_TABLET_ROW_CARD_MAX = 172;

/**
 * Measured from Figma `Frame 2147241009` + design screenshot comparison.
 * Logo sits above Landscape; CTA bottom aligns with mosaic bottom.
 * @see https://www.figma.com/design/pHZN7NM1HgIuUZdu7MFqDV/Client-Design-File?node-id=4538-97338
 */
const FIGMA = {
  width: 1440,
  height: 864,
  padTop: 171,
  mosaicHeight: 522,
  padBottom: 171,
  padSide: 35,
  colGap: 98,
  leftWidth: 500,
  mosaicWidth: 772,
  leftLift: 48,
  mosaicShiftDown: 56,
  /**
   * Card sizes at 1440×864.
   * Short and tall cards are 320 wide. Facilities is 670 = 320 + 30 + 320.
   * Row gap = tall height − top card − bottom card.
   */
  cardColumnWidth: 320,
  mosaicGap: 30,
  /** Tall height − top card − bottom card, rounded so the literal stays exact. */
  rowGap: 26.738357543945,
  tallCardHeight: 497.524658203125,
  cardRadius: 20,
  mosaicRows: 3,
  gapAfterLogo: 50,
  /** Web large hub: inset wordmark below the top edge (1440 reference). Cards stay bottom-aligned. */
  webLogoDownOffset: 28,
  gapAfterEyebrow: 55,
  gapAfterHeadline: 24,
  gapBeforeCta: 60,
  textIndent: 16,
  wordmarkScale: 0.88,
  font3xl: 34,
  fontHeadline: 48,
  ctaHeight: 48,
  ctaWidth: 208,
  ctaGap: 8,
  ctaRadius: 8,
  ctaPadding: 12,
} as const;

const WEB_LOGO_DOWN_MIN = 12;
const WEB_LOGO_DOWN_MAX = 40;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

type HubContent = NonNullable<
  ReturnType<typeof useBloomUniversalHubContent>['hub']
>;

interface HubLayoutMetrics {
  readonly padTop: number;
  readonly padBottom: number;
  readonly padSide: number;
  readonly colGap: number;
  readonly mosaicHeight: number;
  readonly mosaicShiftDown: number;
  readonly bandHeight: number;
  readonly leftLift: number;
  readonly rowHeight: number;
  readonly mosaicGap: number;
  readonly rowGap: number;
  readonly cardColumnWidth: number;
  readonly gapAfterLogo: number;
  readonly gapAfterEyebrow: number;
  readonly gapAfterHeadline: number;
  readonly gapBeforeCta: number;
  readonly gapHeroToMosaic: number;
  readonly stackMinHeight: number;
  readonly textIndent: number;
  readonly wordmarkScale: number;
  readonly font3xl: number;
  readonly fontEyebrow: number;
  readonly fontHeadline: number;
  readonly cardTitleFontSize: number;
  readonly cardContentInset: number;
  readonly viewportHeight: number;
  readonly scrollEnabled: boolean;
  readonly rowCardHeight: number;
  readonly bottomCardHeight: number;
  readonly logoPadTop: number;
  readonly exploreSpacer: number;
}

/** Shrink phone chrome when mosaic + hero exceed the viewport. */
function compressPhoneChromeOverflow(params: {
  readonly contentHeight: number;
  readonly height: number;
  readonly exploreSpacer: number;
  readonly gapAfterLogo: number;
  readonly gapBeforeCta: number;
  readonly padTop: number;
}): {
  readonly exploreSpacer: number;
  readonly gapAfterLogo: number;
  readonly gapBeforeCta: number;
  readonly padTop: number;
} {
  let exploreSpacer = params.exploreSpacer;
  let gapAfterLogo = params.gapAfterLogo;
  let gapBeforeCta = params.gapBeforeCta;
  let padTop = params.padTop;

  if (params.contentHeight <= params.height) {
    return { exploreSpacer, gapAfterLogo, gapBeforeCta, padTop };
  }

  let remaining = params.contentHeight - params.height;
  const fromExplore = Math.min(exploreSpacer - 8, remaining);
  exploreSpacer -= fromExplore;
  remaining -= fromExplore;

  if (remaining > 0) {
    const fromLogoGap = Math.min(Math.max(0, gapAfterLogo - 24), remaining);
    gapAfterLogo -= fromLogoGap;
    remaining -= fromLogoGap;
  }
  if (remaining > 0) {
    const fromCta = Math.min(Math.max(0, gapBeforeCta - 16), remaining);
    gapBeforeCta -= fromCta;
    remaining -= fromCta;
  }
  if (remaining > 0) {
    padTop = Math.max(28, padTop - remaining);
  }

  return { exploreSpacer, gapAfterLogo, gapBeforeCta, padTop };
}

function resolvePhoneHubLayout(
  height: number,
  safeAreaBottom: number,
): HubLayoutMetrics {
  const heightT = clamp((height - 667) / (844 - 667), 0, 1);
  const padTop = Math.round(28 + heightT * 36);
  const safeBottom = Math.max(safeAreaBottom, Platform.OS === 'web' ? 24 : 0);
  const padBottom = Math.round(8 + heightT * 10) + safeBottom;
  const padSide = Math.round(8 + heightT * 4);
  const mosaicGap = 6;
  /** Scale 1 draws the wordmark ink at ~72px; 27px is the mobile logo height. */
  const wordmarkScale = 27 / 72;
  const logoBlock = Math.round(91 * wordmarkScale);
  const gapAfterLogo = Math.round(24 + heightT * 48);
  const gapBeforeCta = Math.round(20 + heightT * 28);
  const fontEyebrow = Math.round(14 + heightT * 4);
  const fontHeadline = Math.round(22 + heightT * 6);
  const font3xl = Math.round(15 + heightT * 3);
  const headlineLine = fontHeadline + 4;
  const copyBlock =
    fontEyebrow +
    12 +
    headlineLine * 2 +
    12 +
    font3xl +
    gapBeforeCta +
    FIGMA.ctaHeight;
  const exploreSpacer = Math.round(4 + heightT * 86);
  const gapHeroToMosaic = Math.round(8 + heightT * 8);

  const tallCardHeight = PHONE_TALL_FACILITIES_CARD.height;
  const minTile = height <= 700 ? 56 : 68;
  const rowCardHeight = Math.max(
    minTile,
    Math.round((tallCardHeight - mosaicGap) / 2),
  );

  let fixedChrome =
    padTop +
    padBottom +
    logoBlock +
    gapAfterLogo +
    copyBlock +
    exploreSpacer +
    gapHeroToMosaic;
  let mosaicBudget = Math.max(
    tallCardHeight + mosaicGap + minTile,
    height - fixedChrome,
  );
  let bottomCardHeight = Math.max(
    minTile,
    mosaicBudget - tallCardHeight - mosaicGap,
  );
  let mosaicHeight = tallCardHeight + mosaicGap + bottomCardHeight;
  const contentHeight = fixedChrome + mosaicHeight;

  const compressed = compressPhoneChromeOverflow({
    contentHeight,
    height,
    exploreSpacer,
    gapAfterLogo,
    gapBeforeCta,
    padTop,
  });

  if (contentHeight > height) {
    fixedChrome =
      compressed.padTop +
      padBottom +
      logoBlock +
      compressed.gapAfterLogo +
      (copyBlock - gapBeforeCta + compressed.gapBeforeCta) +
      compressed.exploreSpacer +
      gapHeroToMosaic;
    mosaicBudget = Math.max(
      tallCardHeight + mosaicGap + minTile,
      height - fixedChrome,
    );
    bottomCardHeight = Math.max(
      minTile,
      mosaicBudget - tallCardHeight - mosaicGap,
    );
    mosaicHeight = tallCardHeight + mosaicGap + bottomCardHeight;
  }

  return {
    padTop: compressed.padTop,
    padBottom,
    padSide,
    colGap: 0,
    mosaicHeight,
    mosaicShiftDown: 0,
    bandHeight: 0,
    leftLift: 0,
    rowHeight: rowCardHeight,
    mosaicGap,
    rowGap: mosaicGap,
    cardColumnWidth: 0,
    gapAfterLogo: compressed.gapAfterLogo,
    gapAfterEyebrow: 12,
    gapAfterHeadline: 12,
    gapBeforeCta: compressed.gapBeforeCta,
    gapHeroToMosaic,
    stackMinHeight: height,
    textIndent: 0,
    wordmarkScale,
    font3xl,
    fontEyebrow,
    fontHeadline,
    cardTitleFontSize: 0,
    cardContentInset: 0,
    viewportHeight: height,
    scrollEnabled: true,
    rowCardHeight,
    bottomCardHeight,
    logoPadTop: 0,
    exploreSpacer: compressed.exploreSpacer,
  };
}

function resolveLargeDesktopHubLayout(
  width: number,
  height: number,
  scaleX: number,
): HubLayoutMetrics {
  /** Cap height scaling so tall viewports do not stretch mosaic rows (Figma is 1440×864). */
  const scaleY = Math.min(height / FIGMA.height, scaleX * 1.08);
  const mosaicGap = FIGMA.mosaicGap * scaleX;
  const rowGap = FIGMA.rowGap * scaleY;
  const cardColumnWidth = FIGMA.cardColumnWidth * scaleX;
  const tallCardHeight = FIGMA.tallCardHeight * scaleY;
  const rowHeight = Math.max(
    96,
    Math.round((tallCardHeight - rowGap) / 2),
  );
  const mosaicHeight = rowHeight * 2 + rowGap;
  const leftLift = Math.round(FIGMA.leftLift * scaleY);
  const mosaicShiftDown = Math.round(FIGMA.mosaicShiftDown * scaleY);
  const bandHeight = mosaicHeight + mosaicShiftDown;
  const padTop = Math.round(16 * scaleY);
  const padBottom = Math.round(16 * scaleY);

  return {
    padTop,
    padBottom,
    padSide: Math.round(FIGMA.padSide * scaleX),
    colGap: Math.round(24 * scaleY),
    mosaicHeight,
    mosaicShiftDown,
    bandHeight,
    leftLift,
    rowHeight,
    mosaicGap,
    rowGap,
    cardColumnWidth,
    gapAfterLogo: Math.round(40 * scaleY),
    gapAfterEyebrow: Math.round(20 * scaleY),
    gapAfterHeadline: Math.round(20 * scaleY),
    gapBeforeCta: Math.round(20 * scaleY),
    /** CTA → mosaic top (stacked desktop: copy sits on the card band). */
    gapHeroToMosaic: mosaicShiftDown,
    stackMinHeight: 0,
    textIndent: Math.round(FIGMA.textIndent * scaleX),
    wordmarkScale: 1,
    font3xl: 34,
    fontEyebrow: 20,
    fontHeadline: 34,
    cardTitleFontSize: 0,
    cardContentInset: 0,
    viewportHeight: height,
    /**
     * Runtime measurement decides scroll — static Figma band height ≈ viewport
     * would always disable scroll even when CMS cards wrap past the band.
     */
    scrollEnabled: false,
    rowCardHeight: rowHeight,
    bottomCardHeight: rowHeight,
    logoPadTop: 0,
    exploreSpacer: 0,
  };
}

function resolveMediumHubLayout(
  width: number,
  height: number,
  scaleX: number,
): HubLayoutMetrics {
  const mosaicGap = 8;
  const padTop = 36;
  const padBottom = 36;
  const padSide = 48;
  const gapHeroToMosaic = 24;
  const logoPadTop = 44;
  const wordmarkApprox = 44;
  const ctaHeight = FIGMA.ctaHeight;
  const ctaNudge = 16;

  const typeT = clamp(
    (width - PHONE_MAX) / (FIGMA.width - PHONE_MAX),
    0,
    1,
  );
  const fontEyebrow = Math.round(20 + typeT * 14);
  const fontHeadline = Math.round(32 + typeT * 16);
  const font3xl = Math.round(18 + typeT * 16);
  const gapAfterEyebrow = 10;
  const gapAfterHeadline = Math.round(12 + typeT * 12);
  const gapBeforeCta = Math.round(16 + typeT * 28);

  const logoBlock = logoPadTop + wordmarkApprox;
  const copyBlock =
    fontEyebrow +
    gapAfterEyebrow +
    (fontHeadline + 4) * 2 +
    gapAfterHeadline +
    font3xl +
    gapBeforeCta +
    ctaHeight +
    ctaNudge;
  const portraitTablet = isPortraitTabletHubViewport(width, height);
  const fixedChrome =
    padTop + padBottom + logoBlock + copyBlock + gapHeroToMosaic;
  const mosaicBudget = Math.max(200, height - fixedChrome);
  const rowCardMax = portraitTablet ? MEDIUM_PORTRAIT_TABLET_ROW_CARD_MAX : 140;
  const rowCardHeight = Math.max(
    72,
    Math.min(rowCardMax, Math.floor((mosaicBudget - 2 * mosaicGap) / 2.72)),
  );
  const midRowHeight = Math.max(64, Math.round(rowCardHeight * 0.72));
  const bottomCardHeight = midRowHeight;
  const mosaicHeight = getMediumMosaicHeight(
    rowCardHeight,
    midRowHeight,
    mosaicGap,
  );

  const cardTitleFontSize = Math.max(
    12,
    Math.min(
      20,
      Math.round(12 + ((width - PHONE_MAX) / (FIGMA.width - PHONE_MAX)) * 8),
    ),
  );
  const cardContentInset = Math.max(
    10,
    Math.min(12, Math.round(10 + cardTitleFontSize * 0.35)),
  );

  return {
    padTop,
    padBottom,
    padSide,
    colGap: 0,
    mosaicHeight,
    mosaicShiftDown: 0,
    bandHeight: 0,
    leftLift: 0,
    rowHeight: rowCardHeight,
    mosaicGap,
    rowGap: mosaicGap,
    cardColumnWidth: 0,
    gapAfterLogo: 0,
    gapAfterEyebrow,
    gapAfterHeadline,
    gapBeforeCta,
    gapHeroToMosaic,
    textIndent: 0,
    wordmarkScale: clamp(0.78 * clamp(scaleX, 0.85, 1.05), 0.7, 0.9),
    font3xl,
    fontEyebrow,
    fontHeadline,
    cardTitleFontSize,
    cardContentInset,
    viewportHeight: height,
    stackMinHeight: height,
    /** Runtime measurement decides scroll when mosaic exceeds the fitted budget. */
    scrollEnabled: false,
    rowCardHeight,
    bottomCardHeight,
    logoPadTop,
    exploreSpacer: 0,
  };
}

/**
 * Bloom Universal hub —
 * - large (≥1024 wide, landscape): copy on top, full-width desktop mosaic (1440 Figma)
 * - medium (700–1023, or portrait ≥1024 e.g. iPad): stacked hero + full-width bento
 * - phone (<700): stacked hero + phone bento
 *
 * Scroll is enabled only when measured content exceeds the viewport (no phantom scrollbar).
 */
/** Ignore sub-pixel / padding rounding so a fitted layout does not get a phantom scrollbar. */
const SCROLL_OVERFLOW_THRESHOLD_PX = 4;

/**
 * Phone and tablet stay scrollable. Large desktop scrolls only when content
 * overflows the measured viewport.
 */
function isHubScrollEnabled(
  isMedium: boolean,
  isPhone: boolean,
  layoutScrollEnabled: boolean,
  measuredScrollEnabled: boolean,
): boolean {
  if (isMedium) {
    return true;
  }
  if (isPhone) {
    return layoutScrollEnabled || measuredScrollEnabled;
  }
  return measuredScrollEnabled;
}

function buildHubScrollContentPadding(
  layout: HubLayoutMetrics,
  hubEdgeToEdge: boolean,
): ViewStyle {
  const verticalPad = hubEdgeToEdge ? 0 : layout.padTop + layout.padBottom;
  const minHeight =
    Platform.OS === 'web'
      ? Math.max(0, layout.viewportHeight - verticalPad)
      : layout.viewportHeight;
  return {
    minHeight,
    paddingTop: hubEdgeToEdge ? 0 : layout.padTop,
    paddingBottom: hubEdgeToEdge ? 0 : layout.padBottom,
    paddingLeft: hubEdgeToEdge ? 0 : layout.padSide,
    paddingRight: hubEdgeToEdge ? 0 : layout.padSide,
  };
}

function renderHubStackVariant(params: Readonly<{
  hub: HubContent;
  exploreTitle: string;
  exploreSubtitle: string;
  isRTL: boolean;
  isPhone: boolean;
  isMedium: boolean;
  layout: HubLayoutMetrics;
  onOpenSpace: (spaceKey: string) => void;
  onCta: () => void;
}>): React.ReactNode {
  const {
    hub,
    exploreTitle,
    exploreSubtitle,
    isRTL,
    isPhone,
    isMedium,
    layout,
    onOpenSpace,
    onCta,
  } = params;

  if (isPhone) {
    return (
      <PhoneHubStack
        hub={hub}
        exploreTitle={exploreTitle}
        exploreSubtitle={exploreSubtitle}
        isRTL={isRTL}
        onOpenSpace={onOpenSpace}
        onCta={onCta}
        wordmarkScale={layout.wordmarkScale}
        fontEyebrow={layout.fontEyebrow}
        fontHeadline={layout.fontHeadline}
        font3xl={layout.font3xl}
        gapAfterLogo={layout.gapAfterLogo}
        gapBeforeCta={layout.gapBeforeCta}
        gapHeroToMosaic={layout.gapHeroToMosaic}
        stackMinHeight={layout.stackMinHeight}
        mosaicGap={layout.mosaicGap}
        rowCardHeight={layout.rowCardHeight}
        bottomCardHeight={layout.bottomCardHeight}
      />
    );
  }

  if (isMedium) {
    return (
      <MediumHubStack
        hub={hub}
        exploreTitle={exploreTitle}
        exploreSubtitle={exploreSubtitle}
        isRTL={isRTL}
        onOpenSpace={onOpenSpace}
        onCta={onCta}
        wordmarkScale={layout.wordmarkScale}
        font3xl={layout.font3xl}
        fontEyebrow={layout.fontEyebrow}
        fontHeadline={layout.fontHeadline}
        gapAfterEyebrow={layout.gapAfterEyebrow}
        gapAfterHeadline={layout.gapAfterHeadline}
        gapBeforeCta={layout.gapBeforeCta}
        gapHeroToMosaic={layout.gapHeroToMosaic}
        stackMinHeight={layout.stackMinHeight}
        mosaicGap={layout.mosaicGap}
        rowCardHeight={layout.rowCardHeight}
        bottomCardHeight={layout.bottomCardHeight}
        titleFontSize={layout.cardTitleFontSize}
        contentInset={layout.cardContentInset}
        logoPadTop={layout.logoPadTop}
      />
    );
  }

  return (
    <LargeDesktopHub
      hub={hub}
      isRTL={isRTL}
      layout={layout}
      onOpenSpace={onOpenSpace}
      onCta={onCta}
    />
  );
}

export function BloomUniversalHubScreen() {
  const { isRTL } = useI18nContext();
  // breakpointWidth/Height are zoom-corrected (same as Universal space screen).
  // Raw visibleHeight under-sizes the band on browser zoom and leaves empty space below.
  const { breakpointWidth: width, breakpointHeight: height } = useWebViewport();
  const insets = useSafeAreaInsets();
  const [scrollViewportHeight, setScrollViewportHeight] = useState(0);
  const [scrollContentHeight, setScrollContentHeight] = useState(0);

  const { hub, isLoading } = useBloomUniversalHubContent();
  const { t, currentLanguage } = useTranslation();

  useCatalogDynatraceView(
    LANDING_PAGE_SCREEN.HUB,
    LANDING_PAGE_ACTION.VIEWED,
    Boolean(hub) && !isLoading,
  );
  const isPhone = width < PHONE_MAX;
  const isLargeDesktop = isLargeDesktopHubViewport(width, height);
  const isMedium = !isPhone && !isLargeDesktop;

  const layout = useMemo((): HubLayoutMetrics => {
    const scaleX = width / FIGMA.width;
    if (isPhone) {
      return resolvePhoneHubLayout(height, insets.bottom);
    }
    if (isLargeDesktop) {
      return resolveLargeDesktopHubLayout(width, height, scaleX);
    }
    return resolveMediumHubLayout(width, height, scaleX);
  }, [width, height, isPhone, isLargeDesktop, insets.bottom]);

  useEffect(() => {
    setScrollViewportHeight(0);
    setScrollContentHeight(0);
  }, [width, height, hub?.spaces]);

  const measuredScrollEnabled =
    scrollViewportHeight > 0 &&
    scrollContentHeight > scrollViewportHeight + SCROLL_OVERFLOW_THRESHOLD_PX;

  /**
   * Phone and tablet (iPad) stay scrollable. Locking scroll sets touch-action:none
   * and crops the mosaic when the visible window is shorter than the layout.
   * Large desktop still waits for a measured overflow so a fitted page has no bar.
   */
  const scrollEnabled = isHubScrollEnabled(
    isMedium,
    isPhone,
    layout.scrollEnabled,
    measuredScrollEnabled,
  );

  const onScrollViewLayout = useCallback(
    (event: { nativeEvent: { layout: { height: number } } }) => {
      setScrollViewportHeight(Math.round(event.nativeEvent.layout.height));
    },
    [],
  );

  const onScrollContentSizeChange = useCallback((_w: number, h: number) => {
    setScrollContentHeight(Math.round(h));
  }, []);

  const openSpace = useCallback(
    (spaceKey: string) => {
      const space = hub?.spaces.find((s) => s.spaceKey === spaceKey);
      trackCatalogEvent({
        screen: LANDING_PAGE_SCREEN.HUB,
        actionName: LANDING_PAGE_ACTION.EXPLORE_CARD_CLICK,
        eventType: 'action',
        properties: landingSpaceDetailProperties({
          spaceKey,
          spaceName: space?.title ?? null,
          experienceName: space?.title ?? null,
          locale: currentLanguage,
        }),
      });
      router.push(resolveBloomUniversalSpaceHref(spaceKey) as never);
    },
    [hub?.spaces, currentLanguage],
  );

  const handleCta = useCallback(() => {
    trackCatalogEvent({
      screen: LANDING_PAGE_SCREEN.HUB,
      actionName: LANDING_PAGE_ACTION.HUB_CTA_CLICK,
      eventType: 'action',
      properties: { locale: currentLanguage },
    });
    const href = WELCOME_PATH;
    if (href.startsWith('/')) {
      router.push(href as never);
      return;
    }
    void Linking.openURL(href);
  }, [hub?.ctaHref, currentLanguage]);

  if (Platform.OS !== 'web') {
    return null;
  }

  // Wait for Contentful — blank until response; fallback only via hook on API error.
  if (isLoading || !hub) {
    return null;
  }

  const hubEdgeToEdge = isPhone || isMedium;
  const scrollPadding = buildHubScrollContentPadding(layout, hubEdgeToEdge);

  const exploreCopy = resolveHubExploreCopy(hub, {
    exploreTitle: t('bloomUniversal.exploreTitle'),
    exploreSubtitle: t('bloomUniversal.exploreSubtitle'),
  });

  const hubStack = renderHubStackVariant({
    hub,
    exploreTitle: exploreCopy.exploreTitle,
    exploreSubtitle: exploreCopy.exploreSubtitle,
    isRTL,
    isPhone,
    isMedium,
    layout,
    onOpenSpace: openSpace,
    onCta: handleCta,
  });

  return (
    <View style={[styles.root, styles.rootPlain]}>
      {isPhone ? null : (
        <BloomUniversalLanguageHeader
          showBackButton={false}
          sideInset={isMedium ? MEDIUM_HUB_EDGE_INSET : layout.padSide}
        />
      )}

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.scrollContent,
          scrollPadding,
          hubEdgeToEdge ? styles.hubScrollEdgeToEdge : null,
          isPhone ? styles.phoneScrollContent : null,
          isLargeDesktop ? styles.hubScrollDesktopBand : null,
        ]}
        scrollEnabled={scrollEnabled}
        showsVerticalScrollIndicator={scrollEnabled}
        bounces={scrollEnabled}
        alwaysBounceVertical={false}
        nestedScrollEnabled
        onLayout={onScrollViewLayout}
        onContentSizeChange={onScrollContentSizeChange}
      >
        {hubStack}
      </ScrollView>
    </View>
  );
}

function LargeDesktopHub({
  hub,
  isRTL,
  layout,
  onOpenSpace,
  onCta,
}: Readonly<{
  hub: HubContent;
  isRTL: boolean;
  layout: HubLayoutMetrics;
  onOpenSpace: (spaceKey: string) => void;
  onCta: () => void;
}>) {
  const placed = placeBloomSpaceGrid(hub.spaces);
  const scaleX = layout.cardColumnWidth / FIGMA.cardColumnWidth;
  const scaleY = layout.viewportHeight / FIGMA.height;
  const gridHeight = bloomSpaceGridHeight(
    placed,
    layout.rowHeight,
    layout.rowGap,
  );

  const logoDownOffset =
    Platform.OS === 'web'
      ? clamp(
          Math.round(FIGMA.webLogoDownOffset * scaleX),
          WEB_LOGO_DOWN_MIN,
          WEB_LOGO_DOWN_MAX,
        )
      : 0;

  const bandMinHeight = Math.max(
    0,
    layout.viewportHeight - layout.padTop - layout.padBottom,
  );

  return (
    <View
      style={[
        styles.columnsBandStacked,
        { minHeight: bandMinHeight, flex: 1 },
      ]}
    >
      <View
        style={[
          styles.desktopHubLogoRow,
          logoDownOffset > 0 ? { paddingTop: logoDownOffset } : null,
          isRTL ? { alignItems: 'flex-end' } : null,
        ]}
        {...(isRTL ? webViewDirProps('ltr') : {})}
      >
        <View
          style={{
            marginLeft: isRTL ? 0 : -16 * layout.wordmarkScale,
            marginRight: isRTL ? -16 * layout.wordmarkScale : 0,
          }}
        >
          <BloomWordmark scale={layout.wordmarkScale} />
        </View>
      </View>

      <View style={[styles.desktopHubBottomBlock, styles.desktopHubBottomPinned]}>
        <View
          style={[
            styles.heroCopyStacked,
            isRTL ? { alignItems: 'flex-end' } : null,
          ]}
          {...(isRTL ? webViewDirProps('ltr') : {})}
        >
          <View style={styles.heroTextBlock}>
          <Typography
            variant="title1"
            style={[
              styles.eyebrow,
              styles.pageCopyEyebrow,
              {
                fontFamily: 'Matter-Medium',
                fontWeight: '500',
                fontSize: 20,
                lineHeight: 20,
                letterSpacing: 0,
                textAlign: isRTL ? 'right' : 'left',
                marginBottom: layout.gapAfterEyebrow,
              },
            ]}
          >
            {hub.experienceEyebrow}
          </Typography>
          <Typography
            variant="body"
            style={[
              styles.headline,
              styles.pageCopyHeadline,
              {
                fontFamily: 'Matter-SemiBold',
                fontWeight: '400',
                fontSize: 34,
                lineHeight: 34,
                letterSpacing: 0,
                textAlign: isRTL ? 'right' : 'left',
                marginBottom: layout.gapAfterHeadline,
              },
            ]}
          >
            {hub.headline}
          </Typography>
          <Pressable
            onPress={onCta}
            accessibilityRole="button"
            accessibilityLabel={hub.ctaLabel}
            {...(isRTL ? webViewDirProps('ltr') : {})}
            style={({ pressed }) => [
              styles.cta,
              {
                alignSelf: isRTL ? 'flex-end' : 'flex-start',
                flexShrink: 0,
                borderRadius: 8 * scaleY,
                paddingTop: 16 * scaleY,
                paddingBottom: 16 * scaleY,
                paddingLeft: 20 * scaleX,
                paddingRight: 20 * scaleX,
                gap: 4 * scaleX,
                backgroundColor: bloomColors.bloomUniversalInk,
              },
              isRTL ? styles.ctaRtl : null,
              pressed ? styles.ctaPressed : null,
            ]}
          >
            <Typography
              variant="headline"
              numberOfLines={1}
              style={[
                styles.ctaText,
                {
                  fontFamily: 'Matter-Medium',
                  fontWeight: '500',
                  fontSize: 22 * scaleY,
                  lineHeight: 22 * scaleY,
                  letterSpacing: 0,
                  textAlign: 'center',
                  color: bloomColors.bloomUniversalOnPhoto,
                },
              ]}
            >
              {hub.ctaLabel}
            </Typography>
            <HubCtaArrow
              isRTL={isRTL}
              color={bloomColors.bloomUniversalOnPhoto}
              {...(hub.arrowIconUrl ? { arrowIconUrl: hub.arrowIconUrl } : {})}
            />
          </Pressable>
          </View>
        </View>

        <View
          style={[
            styles.gridDesktopStacked,
            { height: gridHeight, marginTop: layout.gapHeroToMosaic },
          ]}
        >
        {placed.map((item) => {
          const slotHeight =
            item.rows * layout.rowHeight + (item.rows - 1) * layout.rowGap;
          const columnCount = item.cols / 3;
          const slotWidth =
            columnCount * layout.cardColumnWidth +
            (columnCount - 1) * layout.mosaicGap;
          const slotOffset =
            (item.col / 3) * (layout.cardColumnWidth + layout.mosaicGap);
          const edge = isRTL ? 'right' : 'left';
          return (
            <View
              key={item.space.spaceKey}
              style={{
                position: 'absolute',
                top: item.row * (layout.rowHeight + layout.rowGap),
                height: slotHeight,
                width: slotWidth,
                [edge]: slotOffset,
              }}
            >
              <BloomSpaceCard
                variant="action"
                spaceKey={item.space.spaceKey}
                title={item.space.title}
                gridSpan={item.space.gridSpan}
                width="100%"
                height={slotHeight}
                gutter={0}
                borderRadius={FIGMA.cardRadius}
                {...(hub.arrowIconUrl ? { arrowIconUrl: hub.arrowIconUrl } : {})}
                {...(item.space.iconUrl ? { iconUrl: item.space.iconUrl } : {})}
                {...(item.space.tileImageUrl
                  ? { tileImageUrl: item.space.tileImageUrl }
                  : {})}
                onPress={() => onOpenSpace(item.space.spaceKey)}
              />
            </View>
          );
        })}
        </View>
      </View>
    </View>
  );
}

function MediumHubStack({
  hub,
  exploreTitle,
  exploreSubtitle,
  isRTL,
  onOpenSpace,
  onCta,
  wordmarkScale,
  font3xl,
  fontEyebrow,
  fontHeadline,
  gapAfterEyebrow,
  gapAfterHeadline,
  gapBeforeCta,
  gapHeroToMosaic,
  stackMinHeight,
  mosaicGap,
  rowCardHeight,
  bottomCardHeight,
  titleFontSize,
  contentInset,
  logoPadTop,
}: Readonly<{
  hub: HubContent;
  exploreTitle: string;
  exploreSubtitle: string;
  isRTL: boolean;
  onOpenSpace: (spaceKey: string) => void;
  onCta: () => void;
  wordmarkScale: number;
  font3xl: number;
  fontEyebrow: number;
  fontHeadline: number;
  gapAfterEyebrow: number;
  gapAfterHeadline: number;
  gapBeforeCta: number;
  gapHeroToMosaic: number;
  stackMinHeight: number;
  mosaicGap: number;
  rowCardHeight: number;
  bottomCardHeight: number;
  titleFontSize: number;
  contentInset: number;
  logoPadTop: number;
}>) {
  const heroUrl = resolveBloomUniversalImageUrl(hub.heroBackgroundUrl);

  return (
    <View style={[styles.mediumStack, { minHeight: stackMinHeight }]}>
      <View
        style={[
          styles.phoneHeaderBar,
          { paddingTop: Math.max(12, logoPadTop - 32) },
        ]}
      >
        <View style={styles.phoneHeaderLeading} />
        <View style={styles.phoneHeroLogo}>
          <BloomWordmark scale={wordmarkScale} />
        </View>
        <View style={styles.phoneHeaderTrailing} />
      </View>

      <View style={[styles.phoneHero, styles.mediumHero]}>
        {heroUrl ? (
          <Image
            source={{ uri: heroUrl }}
            style={styles.phoneHeroImage}
            resizeMode="cover"
            accessibilityIgnoresInvertColors
          />
        ) : (
          <View style={[styles.phoneHeroImage, styles.phoneHeroFallback]} />
        )}
        <View pointerEvents="none" style={styles.phoneHeroScrim} />
        <View
          style={[
            styles.phoneHeroCopyOverlay,
            { paddingHorizontal: MEDIUM_HUB_EDGE_INSET },
          ]}
        >
          <View
            style={[
              styles.heroTextBlock,
              styles.phoneHeroCopyBlock,
              { gap: 8 },
              isRTL ? styles.phoneHeroCopyRtl : null,
            ]}
            {...(isRTL ? webViewDirProps('ltr') : {})}
          >
            <View style={styles.mediumTitleGroup}>
              <Typography
                variant="title1"
                style={[
                  styles.eyebrow,
                  styles.phoneOnHero,
                  {
                    fontFamily: 'Matter-Medium',
                    fontWeight: '500',
                    fontSize: fontEyebrow,
                    lineHeight: fontEyebrow,
                    letterSpacing: 0,
                    textAlign: isRTL ? 'right' : 'left',
                    marginBottom: gapAfterEyebrow,
                  },
                ]}
              >
                {hub.experienceEyebrow}
              </Typography>
              <Typography
                variant="body"
                style={[
                  styles.headline,
                  styles.phoneOnHero,
                  {
                    fontFamily: 'Matter-SemiBold',
                    fontWeight: '400',
                    fontSize: fontHeadline,
                    lineHeight: fontHeadline + 4,
                    letterSpacing: 0,
                    textAlign: isRTL ? 'right' : 'left',
                    marginBottom: gapAfterHeadline,
                  },
                ]}
              >
                {hub.headline}
              </Typography>
            </View>
            <Typography
              variant="title1"
              style={[
                styles.phoneSubheadline,
                {
                  fontSize: font3xl,
                  lineHeight: font3xl + 4,
                  textAlign: isRTL ? 'right' : 'left',
                  marginBottom: gapBeforeCta,
                },
              ]}
            >
              {hub.subheadline}
            </Typography>
            <Pressable
              onPress={onCta}
              accessibilityRole="button"
              accessibilityLabel={hub.ctaLabel}
              {...(isRTL ? webViewDirProps('ltr') : {})}
              style={({ pressed }) => [
                styles.phoneCta,
                { marginTop: Math.max(0, gapBeforeCta - 12) },
                isRTL ? styles.phoneCtaRtl : null,
                pressed ? styles.ctaPressed : null,
              ]}
            >
              <Typography
                variant="headline"
                numberOfLines={1}
                style={styles.phoneCtaText}
              >
                {hub.ctaLabel}
              </Typography>
              <HubCtaArrow
                isRTL={isRTL}
                color={bloomColors.bloomUniversalInkStrong}
                {...(hub.arrowIconUrl ? { arrowIconUrl: hub.arrowIconUrl } : {})}
              />
            </Pressable>
          </View>
        </View>
      </View>

      <View
        style={[
          styles.phoneSheet,
          { paddingHorizontal: MEDIUM_HUB_EDGE_INSET },
        ]}
      >
        <Typography variant="title2" style={styles.phoneExploreTitle}>
          {exploreTitle}
        </Typography>
        <Typography variant="body" style={styles.phoneExploreBody}>
          {exploreSubtitle}
        </Typography>
        <View style={{ marginTop: gapHeroToMosaic }}>
          <BloomUniversalPhoneMosaic
            spaces={hub.spaces}
            isRTL={isRTL}
            onOpenSpace={onOpenSpace}
            gutter={mosaicGap}
            rowCardHeight={rowCardHeight}
            bottomCardHeight={bottomCardHeight}
            titleFontSize={titleFontSize}
            contentInset={contentInset}
            {...(hub.arrowIconUrl ? { arrowIconUrl: hub.arrowIconUrl } : {})}
            variant="medium"
          />
        </View>
      </View>
    </View>
  );
}

function PhoneHubStack({
  hub,
  exploreTitle,
  exploreSubtitle,
  isRTL,
  onOpenSpace,
  onCta,
  wordmarkScale,
  fontEyebrow,
  fontHeadline,
  font3xl,
  gapAfterLogo,
  gapBeforeCta,
  gapHeroToMosaic,
  stackMinHeight,
  mosaicGap,
  rowCardHeight,
  bottomCardHeight,
}: Readonly<{
  hub: HubContent;
  exploreTitle: string;
  exploreSubtitle: string;
  isRTL: boolean;
  onOpenSpace: (spaceKey: string) => void;
  onCta: () => void;
  wordmarkScale: number;
  fontEyebrow: number;
  fontHeadline: number;
  font3xl: number;
  gapAfterLogo: number;
  gapBeforeCta: number;
  gapHeroToMosaic: number;
  stackMinHeight: number;
  mosaicGap: number;
  rowCardHeight: number;
  bottomCardHeight: number;
}>) {
  const heroUrl = resolveBloomUniversalImageUrl(hub.heroBackgroundUrl);
  const phone = hub.phoneNumber?.trim() ?? '';

  return (
    <View style={[styles.phoneStack, { height: stackMinHeight, minHeight: stackMinHeight }]}>
      <View style={styles.phoneHeaderBar}>
        <View style={styles.phoneHeaderLeading}>
          <LanguageToggle color="fontPrimary" />
        </View>
        <View style={styles.phoneHeroLogo}>
          <BloomWordmark scale={wordmarkScale} />
        </View>
        <View style={styles.phoneHeaderTrailing}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Call"
            disabled={!phone}
            onPress={() => {
              if (phone) {
                openPhoneDialer(phone);
              }
            }}
            style={[
              styles.phoneCircleButton,
              Platform.OS === 'web'
                ? ({
                    boxShadow: `0px 0px 19px 0px ${bloomColors.bloomUniversalCallShadow}`,
                  } as ViewStyle)
                : {
                    shadowColor: bloomColors.bloomUniversalEyebrow,
                    shadowOffset: { width: 0, height: 0 },
                    shadowOpacity: 0.149,
                    shadowRadius: 19,
                    elevation: 4,
                  },
            ]}
          >
            <BloomCmsIcon
              url={hub.phoneIconUrl}
              size={18}
              fallback={
                <BloomSpacePhoneIcon
                  size={18}
                  color={bloomColors.bloomUniversalInkStrong}
                />
              }
            />
          </Pressable>
        </View>
      </View>
      <View style={styles.phoneHero}>
        {heroUrl ? (
          <Image
            source={{ uri: heroUrl }}
            style={styles.phoneHeroImage}
            resizeMode="cover"
            accessibilityIgnoresInvertColors
          />
        ) : (
          <View style={[styles.phoneHeroImage, styles.phoneHeroFallback]} />
        )}
        <View pointerEvents="none" style={styles.phoneHeroScrim} />
        <View style={[styles.phoneHeroCopyOverlay, styles.phoneHeroCopy]}>
          <View
            style={[
              styles.heroTextBlock,
              styles.phoneHeroCopyBlock,
              { gap: 8 },
              isRTL ? styles.phoneHeroCopyRtl : null,
            ]}
            {...(isRTL ? webViewDirProps('ltr') : {})}
          >
          <Typography
            variant="title1"
            style={[
              styles.eyebrow,
              styles.phoneOnHero,
              {
                fontFamily: 'Matter-SemiBold',
                fontWeight: '400',
                fontSize: 12,
                lineHeight: 16,
                letterSpacing: 0,
                textAlign: isRTL ? 'right' : 'left',
              },
            ]}
          >
            {hub.experienceEyebrow}
          </Typography>
          <Typography
            variant="body"
            style={[
              styles.headline,
              styles.phoneOnHero,
              {
                fontFamily: 'Matter-Medium',
                fontWeight: '400',
                fontSize: 28,
                lineHeight: 28,
                letterSpacing: 0,
                textAlign: isRTL ? 'right' : 'left',
              },
            ]}
          >
            {hub.headline}
          </Typography>
          <Typography
            variant="title1"
            style={[
              styles.phoneSubheadline,
              {
                fontSize: font3xl,
                lineHeight: font3xl + 4,
                textAlign: isRTL ? 'right' : 'left',
              },
            ]}
          >
            {hub.subheadline}
          </Typography>
          <Pressable
            onPress={onCta}
            accessibilityRole="button"
            accessibilityLabel={hub.ctaLabel}
            {...(isRTL ? webViewDirProps('ltr') : {})}
            style={({ pressed }) => [
              styles.phoneCta,
              { marginTop: Math.max(0, gapBeforeCta - 12) },
              isRTL ? styles.phoneCtaRtl : null,
              pressed ? styles.ctaPressed : null,
            ]}
          >
            <Typography
              variant="headline"
              numberOfLines={1}
              style={styles.phoneCtaText}
            >
              {hub.ctaLabel}
            </Typography>
            <HubCtaArrow
              isRTL={isRTL}
              color={bloomColors.bloomUniversalInkStrong}
              {...(hub.arrowIconUrl ? { arrowIconUrl: hub.arrowIconUrl } : {})}
            />
          </Pressable>
          </View>
        </View>
      </View>

      <View style={styles.phoneSheet}>
        <Typography variant="title2" style={styles.phoneExploreTitle}>
          {exploreTitle}
        </Typography>
        <Typography variant="body" style={styles.phoneExploreBody}>
          {exploreSubtitle}
        </Typography>
        <View style={{ marginTop: gapHeroToMosaic }}>
          <BloomUniversalPhoneMosaic
            spaces={hub.spaces}
            isRTL={isRTL}
            onOpenSpace={onOpenSpace}
            gutter={mosaicGap}
            rowCardHeight={rowCardHeight}
            {...(hub.arrowIconUrl ? { arrowIconUrl: hub.arrowIconUrl } : {})}
            bottomCardHeight={Math.min(bottomCardHeight, 108)}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: '100%',
    minHeight: '100%',
    ...(Platform.OS === 'web'
      ? ({
        // Use --bloom-100dvh instead of 100dvh directly.
        // dvh/vh units bypass the CSS zoom context on <html>, so at e.g. 125%
        // browser zoom (css zoom: 0.8) a raw 100dvh container renders at only
        // 80% of the screen leaving a white gap. The custom property is set by
        // GlobalWebStyles to calc(100dvh / scale), which after zoom = viewport.
        minHeight: 'var(--bloom-100dvh, 100dvh)',
        height: 'var(--bloom-100dvh, 100dvh)',
        maxHeight: 'var(--bloom-100dvh, 100dvh)',
        overflow: 'hidden',
      } as object)
      : { overflow: 'hidden' }),
  },
  rootPlain: {
    backgroundColor: bloomColors.bloomUniversalPage,
  },
  scroll: {
    flex: 1,
    minHeight: 0,
    zIndex: 2,
    ...(Platform.OS === 'web'
      ? ({
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
      } as object)
      : null),
  },
  scrollContent: {
    flexGrow: 1,
  },
  /** Desktop hub: fill viewport so logo top / mosaic bottom band can flex. */
  hubScrollDesktopBand: {
    flexGrow: 1,
    ...(Platform.OS === 'web'
      ? ({
          display: 'flex',
          flexDirection: 'column',
        } as object)
      : null),
  },
  /** Large home: copy on top, mosaic full width underneath. */
  columnsBandStacked: {
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    flexGrow: 1,
  },
  heroCopyStacked: {
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
    alignItems: 'flex-start',
  },
  desktopHubLogoRow: {
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
    alignItems: 'flex-start',
  },
  desktopHubVerticalSpacer: {
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
  },
  desktopHubBottomBlock: {
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
    alignSelf: 'stretch',
  },
  /** Hero + mosaic band pinned to the bottom of the desktop viewport column. */
  desktopHubBottomPinned: {
    marginTop: 'auto',
  },
  heroTextBlock: {
    width: '100%',
    alignItems: 'flex-start',
  },
  phoneScrollContent: {
    flexGrow: 1,
    paddingBottom: 0,
  },
  /** Phone + medium: scroll content spans viewport width (no side padding). */
  hubScrollEdgeToEdge: {
    width: '100%',
    alignSelf: 'stretch',
    ...(Platform.OS === 'web'
      ? ({
          maxWidth: '100%',
        } as object)
      : null),
  },
  phoneStack: {
    width: '100%',
    flexGrow: 1,
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  phoneTopCluster: {
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
  },
  phoneLogoTop: {
    width: '100%',
    alignItems: 'center',
  },
  phoneHero: {
    width: '100%',
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
    position: 'relative',
    overflow: 'hidden',
  },
  /** Vertically centers hero copy; horizontal alignment stays start (LTR left). */
  phoneHeroCopyOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
    justifyContent: 'center',
    alignItems: 'stretch',
  },
  phoneHeroCopyBlock: {
    width: '100%',
    alignItems: 'flex-start',
  },
  phoneHeroCopyRtl: {
    alignItems: 'flex-end',
  },
  phoneHeroImage: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
  },
  phoneHeroFallback: {
    backgroundColor: bloomColors.bloomUniversalImageFallback,
  },
  phoneHeroScrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: bloomColors.bloomUniversalEyebrow,
    opacity: 0.28,
  },
  phoneHeaderBar: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 12,
    paddingBottom: 12,
    paddingHorizontal: 16,
    backgroundColor: bloomColors.bloomUniversalPage,
    zIndex: 2,
  },
  phoneHeroLogo: {
    flexShrink: 0,
    alignItems: 'center',
  },
  phoneHeaderTrailing: {
    flex: 1,
    minHeight: 42,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  phoneHeaderLeading: {
    flex: 1,
    minHeight: 42,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  phoneCircleButton: {
    width: 42,
    height: 42,
    borderRadius: 21,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: bloomColors.bloomUniversalPage,
    borderWidth: 1,
    borderColor: bloomColors.bloomUniversalMuted,
  },
  phoneHeroCopy: {
    paddingHorizontal: 16,
  },
  phoneOnHero: {
    color: bloomColors.bloomUniversalOnPhoto,
  },
  phoneSubheadline: {
    fontFamily: 'Matter-Medium',
    fontWeight: '400',
    letterSpacing: 0,
    color: bloomColors.bloomUniversalOnPhoto,
  },
  phoneCta: {
    alignSelf: 'flex-start',
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    minHeight: 34,
    backgroundColor: bloomColors.bloomUniversalPage,
    borderRadius: 4,
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 12,
    paddingRight: 12,
  },
  phoneCtaText: {
    fontFamily: 'Matter-Medium',
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 18,
    letterSpacing: 0,
    flexShrink: 0,
    color: bloomColors.bloomUniversalInkStrong,
  },
  /** Arabic: CTA on the physical right; label right, arrow on the left. */
  phoneCtaRtl: {
    alignSelf: 'flex-end',
    flexDirection: 'row-reverse',
  },
  phoneSheet: {
    flexGrow: 0,
    flexShrink: 0,
    width: '100%',
    marginTop: -20,
    zIndex: 3,
    backgroundColor: bloomColors.bloomUniversalPage,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    overflow: 'hidden',
    paddingTop: 20,
    paddingHorizontal: 16,
    paddingBottom: 12,
    ...(Platform.OS === 'web'
      ? ({
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          overflow: 'hidden',
        } as object)
      : null),
  },
  phoneExploreTitle: {
    fontFamily: 'Matter-Medium',
    fontWeight: '400',
    fontSize: 20,
    lineHeight: 20,
    letterSpacing: 0,
    color: bloomColors.bloomUniversalInk,
  },
  phoneExploreBody: {
    marginTop: 8,
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    fontSize: 12,
    lineHeight: 12,
    letterSpacing: 0,
    color: bloomColors.bloomUniversalInk,
  },
  mediumStack: {
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
    flexGrow: 1,
    flexDirection: 'column',
    ...(Platform.OS === 'web'
      ? ({
          width: '100%',
          maxWidth: '100%',
        } as object)
      : null),
  },
  /** Hero photo + scrim (same asset as phone hub). */
  mediumHero: {
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 280,
  },
  /** Eyebrow + headline read as one title block. */
  mediumTitleGroup: {
    width: '100%',
  },
  /** Shared type scale — color comes from page vs hero overrides. */
  eyebrow: {
    fontFamily: 'Matter-Medium',
    fontWeight: '400',
    letterSpacing: 0,
    width: '100%',
  },
  headline: {
    fontFamily: 'Matter-Bold',
    fontWeight: '400',
    letterSpacing: 0,
    width: '100%',
  },
  /** White-page hub copy (medium + large desktop). */
  pageCopyEyebrow: {
    color: bloomColors.bloomUniversalEyebrow,
  },
  pageCopyHeadline: {
    color: bloomColors.bloomUniversalInk,
  },
  cta: {
    alignSelf: 'flex-start',
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    backgroundColor: bloomColors.bloomUniversalInk,
    borderRadius: FIGMA.ctaRadius,
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 20,
    paddingRight: 20,
  },
  ctaPressed: {
    opacity: 0.85,
  },
  /** Arabic: label on the right, arrow on the left. */
  ctaRtl: {
    flexDirection: 'row-reverse',
  },
  ctaText: {
    fontFamily: 'Matter-SemiBold',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 16,
    letterSpacing: 0,
    textAlign: 'center',
    flexShrink: 0,
    color: bloomColors.bloomUniversalOnPhoto,
  },
  chevronFlip: {
    transform: [{ scaleX: -1 }],
  },
  gridDesktopStacked: {
    position: 'relative',
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    alignSelf: 'stretch',
  },
});

export default BloomUniversalHubScreen;
